import { Component, OnInit, OnDestroy, ViewChild, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { initItemsInvoiceParams, NovaPoshtaService } from '../../services/nova-poshta.service';
import { IParams } from '../../model/http';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { AppTableSource, ColumnSortType } from '../../model/table.model';
import { NotificationService } from '../../services/notifications.service';
import { IntegrationService } from '../../services/integration.service';
import { BaseService } from '../../services/base.service';
import { tableHeaders, NP_HOTKEYS } from './np-tab-tracker-invoice.interface';
import { CreateTrackInvoiceComponent } from '../../dialogs/create-track-invoice/create-track-invoice.component';
import { MatDialog } from '@angular/material/dialog';
import { ModalService } from '../../services/modal.service';
import { parseNovaPoshta, parseNovaPoshtaStatus } from '../../helpers';
import { DialogService } from '../../services/dialog.service';
import { activeTranslations, LOGISTIC_TYPES } from '../../enum/constants';
import { SelectionModel } from '@angular/cdk/collections';
import { SearchComponent } from '../../components/search/search.component';

@Component({
  selector: 'app-np-tab-tracker-invoice',
  templateUrl: './np-tab-tracker-invoice.component.html',
  styleUrls: ['./np-tab-tracker-invoice.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NpTabTrackerInvoiceComponent implements OnInit, OnDestroy {
  @ViewChild(SearchComponent) searchComponent!: SearchComponent;

  visibleColumns = tableHeaders.map((v) => v.value);
  public isLoader: boolean = false;
  private readonly destroy$: Subject<null> = new Subject<null>();
  selectionTable = new SelectionModel<any>(true, []);

  // Variables
  private itemsRequest: any;
  private dataSort: any;
  dataSource$: Observable<AppTableSource> = this.novaPoshtaService.itemsInvoices$.pipe(
    map((data) => {
      tableHeaders.map((column) => {
        column.sort = ColumnSortType.NONE;
        if (column.value === this.dataSort?.active) {
          column.sort = this.dataSort.direction;
        }
        return column;
      });
      return {
        headers: tableHeaders,
        rows: data.items.map((v: any) => {
          return {
            ...v,
            status: parseNovaPoshtaStatus(v?.np_data?.StatusCode, v?.np_data?.ActualDeliveryDate),
            np_status: v?.np_data?.Status,
            np_delivery_date: v?.np_data?.ActualDeliveryDate,
            np_scheduled_delivery_date: parseNovaPoshta(v?.np_data?.ScheduledDeliveryDate),
          };
        }),
        pageSize: localStorage?.getItem('channelTake') || data?.size || 0,
        pageIndex: data.page ? data.page - 1 : 0,
        length: data.total || 0,
      };
    }),
  );
  private timerSearch: any;

  bulkActionList: any[] = [{ label: 'delete', value: 'DELETE' }];

  constructor(
    private readonly notificationService: NotificationService,
    private readonly integrationService: IntegrationService,
    private readonly novaPoshtaService: NovaPoshtaService,
    public readonly baseService: BaseService,
    public readonly dialog: MatDialog,
    public readonly modalService: ModalService,
    private readonly dialogService: DialogService,
  ) {}

  getItemsWithUpdatedParams(params: IParams, isLoader = false) {
    if (this.itemsRequest) {
      this.itemsRequest.unsubscribe();
    }
    this.isLoader = isLoader;
    this.selectionTable.clear();
    this.itemsRequest = this.novaPoshtaService
      .setQueryListInvoicesParams(params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (res) => {
          this.isLoader = false;
          if (!res.items.length && res.page > 1) {
            this.getItemsWithUpdatedParams(
              {
                page: res.page - 1,
                take: res.take,
              },
              true,
            );
          }
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  ngOnInit() {
    this.isLoader = true;
    this.novaPoshtaService
      .loadListInvoices()
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: () => {
          this.isLoader = false;
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onSortChange(sortState: any) {
    this.dataSort = sortState;
    this.getItemsWithUpdatedParams({
      sort: sortState.active,
      order: sortState.direction,
    });
  }

  onPaginationChange(paginationDetails: any) {
    const { pageIndex, pageSize } = paginationDetails;
    localStorage.removeItem('invoiceTake');
    localStorage.setItem('invoiceTake', pageSize);
    this.novaPoshtaService.updateInvoiceTake();
    const page = pageIndex + 1;
    const take = pageSize;
    this.getItemsWithUpdatedParams({ page, take }, true);
  }

  onActionChange(event: any) {
    if (event.label === 'DELETE') {
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.ttn_delete',
      });
      dialogRef.subscribe((result) => {
        if (result) {
          this.novaPoshtaService
            .deleteInvoice(event.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.getItemsWithUpdatedParams({});
              },
            });
        }
      });
    }

    if (event.label === 'EDIT') {
      const dialogData = {
        title: 'update',
        translate: true,
        component: CreateTrackInvoiceComponent,
        displayCenter: 'display-center',
        width: '300px',
        data: {
          is_update: true,
          id: event.id,
          ttn: event.ttn,
          comments: event.comments,
          type: LOGISTIC_TYPES.NOVA_POSHTA,
        },
      };

      this.modalService.openModal(dialogData).subscribe((result) => {
        if (result) {
          this.getItemsWithUpdatedParams({});
        }
      });
    }
  }

  onSearchChange(search: string) {
    if (this.timerSearch) clearTimeout(this.timerSearch);
    this.timerSearch = setTimeout(() => {
      this.getItemsWithUpdatedParams(
        {
          ...initItemsInvoiceParams,
          search,
        },
        true,
      );
    }, 500);
  }

  onAdd() {
    const dialogData = {
      title: 'add_ttn',
      translate: true,
      component: CreateTrackInvoiceComponent,
      displayCenter: 'display-center',
      width: '300px',
      data: {
        type: LOGISTIC_TYPES.NOVA_POSHTA,
      },
    };

    this.modalService.openModal(dialogData).subscribe((result) => {
      if (result) {
        this.getItemsWithUpdatedParams({});
      }
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Ігноруємо події, якщо фокус на полі вводу (крім спеціальних випадків)
    const target = event.target as HTMLElement;
    const isInputFocused = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    // Alt+N (Windows) / Option+N (Mac) - Додати ТТН
    if (event.altKey && event.keyCode == 78 && !isInputFocused) {
      event.preventDefault();
      event.stopPropagation();
      this.onAdd();
      return;
    }

    // Alt+F (Windows) / Option+F (Mac) - Фокус на пошук
    if (event.altKey && event.keyCode == 70) {
      event.preventDefault();
      event.stopPropagation();
      this.focusSearch();
      return;
    }

    // Escape - Скасувати виділення (працює завжди)
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.selectionTable.selected.length > 0) {
        this.selectionTable.clear();
      }
      return;
    }
  }

  private focusSearch(): void {
    if (this.searchComponent && this.searchComponent.inputRef) {
      this.searchComponent.inputRef.nativeElement.focus();
    }
  }

  protected isWindows(): boolean {
    return /Windows|Win32|Win64/.test(window.navigator.userAgent);
  }

  protected getModifierKey(): string {
    return this.isWindows() ? 'Alt' : 'Option';
  }

  bulkActionPopup(event: any) {
    if (event.value === 'DELETE') {
      const ids = this.selectionTable.selected.map((v) => v.id);
      this.novaPoshtaService.deleteBulk(ids).subscribe({
        next: (res) => {
          if (res?.length) {
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_deleted,
            );
            this.getItemsWithUpdatedParams({});
          }
        },
        error: (res) => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_while_delete_2,
          );
        },
      });
    }
  }
}
